/**
 * Slice 55 / T1–T5 — start a Test clean. One button on the property page and on Jobs; a short
 * first-time note; then a setup sheet where every answer is a tap. Start creates the pretend turnover
 * and opens the real cleaner screens on the owner's own phone.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Play, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAccessibleProperties } from "@/hooks/useAccessibleProperties";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { toast } from "@/hooks/use-toast";
import {
  DEFAULT_TEST_SETUP,
  TEST_CHECKOUT_TIMES,
  createTestClean,
  hasSeenTestCleanNote,
  markTestCleanNoteSeen,
  type TestCleanSetup,
} from "@/lib/testClean";

interface Props {
  /** Preselected property (the property page). From Jobs, leave empty and the sheet asks first. */
  propertyId?: string | null;
  variant: "property" | "jobs";
  className?: string;
}

function YesNo({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  const pill = (on: boolean) =>
    cn(
      "flex h-12 w-[66px] items-center justify-center rounded-xl border-[1.5px] text-[16px] font-bold",
      on ? "border-[#0f1720] bg-[#0f1720] text-white" : "border-[#d7e0e6] bg-white text-[#0f1720]",
    );
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-[16px] font-bold text-[#0f1720]">{label}</div>
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange(true)} className={pill(value === true)}>
          Yes
        </button>
        <button type="button" onClick={() => onChange(false)} className={pill(value === false)}>
          No
        </button>
      </div>
    </div>
  );
}

export function TestCleanLauncher({ propertyId = null, variant, className }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { properties } = useAccessibleProperties();

  const [noteOpen, setNoteOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(propertyId);
  const [listOpen, setListOpen] = useState(!propertyId);
  const [setup, setSetup] = useState<TestCleanSetup>(DEFAULT_TEST_SETUP);
  const [starting, setStarting] = useState(false);

  const picked = properties.find((p) => p.id === pickedId) ?? null;

  const open = () => {
    setPickedId(propertyId);
    setListOpen(!propertyId);
    if (hasSeenTestCleanNote()) setSetupOpen(true);
    else setNoteOpen(true);
  };

  const start = async () => {
    if (!picked || !user?.id || starting) return;
    setStarting(true);
    try {
      const { taskId } = await createTestClean({ propertyId: picked.id, userId: user.id, setup });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", picked.id] }),
        queryClient.invalidateQueries({ queryKey: ["my-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["reservations", picked.id] }),
      ]);
      setSetupOpen(false);
      navigate(`/tasks/run/${picked.id}?taskId=${taskId}`);
    } catch (err) {
      toast({
        title: "Couldn’t start the test clean",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setStarting(false);
    }
  };

  const noteBody = (
    <div className="flex flex-col gap-4 px-5 pb-6 pt-2">
      <div className="text-[24px] font-bold tracking-[-0.4px] text-[#0f1720]">Test clean</div>
      <div className="text-[16px] font-medium text-[#475569]">This runs like a real clean but is marked Test clean.</div>
      <div className="flex flex-col gap-3">
        {[
          "You go through every cleaner screen, in the real order, on this phone.",
          "Photos, counts and problems are saved and flagged like a real clean.",
          "It shows in Reports under your name. Delete it any time. It never counts in stats or billing.",
        ].map((line, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e2eeed] text-[14px] font-bold text-[#15776d]">
              {i + 1}
            </div>
            <div className="text-[15.5px] font-medium leading-snug text-[#0f1720]">{line}</div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => {
          markTestCleanNoteSeen();
          setNoteOpen(false);
          setSetupOpen(true);
        }}
        className="flex h-[58px] items-center justify-center rounded-2xl bg-[#24a89a] text-[17.5px] font-bold text-white"
      >
        Got it
      </button>
    </div>
  );

  const setupBody = (
    <div className="flex max-h-[85dvh] flex-col">
      <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-2">
        <div>
          <div className="text-[24px] font-bold tracking-[-0.4px] text-[#0f1720]">Test clean</div>
          <div className="text-[14px] font-medium text-[#64748b]">Set up a pretend turnover</div>
        </div>
        <button
          type="button"
          onClick={() => setSetupOpen(false)}
          aria-label="Close"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f1f5f7]"
        >
          <X className="h-5 w-5" strokeWidth={2.2} />
        </button>
      </div>
      <div className="flex flex-col gap-5 overflow-y-auto px-5 pb-4">
        <div className="flex flex-col gap-2">
          <div className="text-[13px] font-bold text-[#8494a2]">Property</div>
          {picked && !listOpen ? (
            <button
              type="button"
              onClick={() => setListOpen(true)}
              className="flex items-center justify-between gap-3 rounded-[14px] border-[1.5px] border-[#d7e0e6] px-4 py-3 text-left"
            >
              <div className="min-w-0">
                <div className="truncate text-[16px] font-bold text-[#0f1720]">{picked.name}</div>
                {picked.address ? <div className="truncate text-[13px] font-medium text-[#64748b]">{picked.address}</div> : null}
              </div>
              <div className="text-[14px] font-bold text-[#15776d]">Change</div>
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              {properties.map((p) => {
                const on = p.id === pickedId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setPickedId(p.id);
                      setListOpen(false);
                    }}
                    className={cn(
                      "flex min-h-[60px] items-center gap-3 rounded-[14px] border-[1.5px] px-4 py-2.5 text-left",
                      on ? "border-[#24a89a] bg-[#f2f8f7]" : "border-[#d7e0e6] bg-white",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[16px] font-bold text-[#0f1720]">{p.name}</div>
                      {p.address ? <div className="truncate text-[13px] font-medium text-[#64748b]">{p.address}</div> : null}
                    </div>
                    <span className={cn("h-[22px] w-[22px] shrink-0 rounded-full", on ? "border-[7px] border-[#24a89a]" : "border-2 border-[#c3ced6]")} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="text-[16px] font-bold text-[#0f1720]">Guests</div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSetup((s) => ({ ...s, guests: Math.max(1, s.guests - 1) }))}
              className="flex h-12 w-12 items-center justify-center rounded-xl border-[1.5px] border-[#d7e0e6] text-[22px] font-bold"
            >
              –
            </button>
            <div className="w-8 text-center text-[20px] font-bold">{setup.guests}</div>
            <button
              type="button"
              onClick={() => setSetup((s) => ({ ...s, guests: Math.min(12, s.guests + 1) }))}
              className="flex h-12 w-12 items-center justify-center rounded-xl border-[1.5px] border-[#d7e0e6] text-[22px] font-bold"
            >
              +
            </button>
          </div>
        </div>

        <YesNo label="Has the guest left?" value={setup.guestLeft} onChange={(v) => setSetup((s) => ({ ...s, guestLeft: v }))} />
        <YesNo label="Same-day check-in?" value={setup.sameDay} onChange={(v) => setSetup((s) => ({ ...s, sameDay: v }))} />
        <YesNo label="Extra dirty?" value={setup.extraDirty} onChange={(v) => setSetup((s) => ({ ...s, extraDirty: v }))} />

        <div className="flex flex-col gap-2">
          <div className="text-[16px] font-bold text-[#0f1720]">Check-out time</div>
          <div className="grid grid-cols-4 gap-2">
            {TEST_CHECKOUT_TIMES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setSetup((s) => ({ ...s, checkOutTime: t }))}
                className={cn(
                  "flex h-12 items-center justify-center rounded-xl border-[1.5px] text-[16px] font-bold",
                  setup.checkOutTime === t ? "border-[#0f1720] bg-[#0f1720] text-white" : "border-[#d7e0e6] bg-white text-[#0f1720]",
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-[#e6edf1] px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3.5">
        <button
          type="button"
          disabled={!picked || starting}
          onClick={() => void start()}
          className={cn(
            "flex h-[58px] items-center justify-center rounded-2xl text-[17.5px] font-bold",
            picked ? "bg-[#24a89a] text-white" : "bg-[#d7e0e6] text-[#64748b]",
          )}
        >
          {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Start test clean"}
        </button>
        <div className="text-center text-[13px] font-medium text-[#64748b]">
          {picked ? "Takes about as long as a real clean. End it any time." : "Pick a property first"}
        </div>
      </div>
    </div>
  );

  const sheet = (isOpen: boolean, onOpenChange: (o: boolean) => void, body: React.ReactNode, label: string) =>
    isMobile ? (
      <Drawer open={isOpen} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92dvh]">
          <DrawerTitle className="sr-only">{label}</DrawerTitle>
          {body}
        </DrawerContent>
      </Drawer>
    ) : (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[420px] p-0">
          <DialogTitle className="sr-only">{label}</DialogTitle>
          {body}
        </DialogContent>
      </Dialog>
    );

  return (
    <>
      {variant === "property" ? (
        <button
          type="button"
          onClick={open}
          className={cn(
            "flex h-10 shrink-0 items-center gap-2 rounded-xl border-[1.5px] border-[#0f1720] bg-white px-3 text-[14px] font-bold text-[#0f1720] hover:bg-[#f4f7f9]",
            className,
          )}
        >
          <Play className="h-3.5 w-3.5 fill-current" />
          Test clean
        </button>
      ) : (
        <button
          type="button"
          onClick={open}
          className={cn(
            "flex h-10 shrink-0 items-center gap-2 rounded-xl border-[1.5px] border-[#0f1720] bg-white px-3.5 text-[14px] font-bold text-[#0f1720] hover:bg-[#f4f7f9]",
            className,
          )}
        >
          <Play className="h-3.5 w-3.5 fill-current" />
          Test clean
        </button>
      )}
      {sheet(noteOpen, setNoteOpen, noteBody, "Test clean")}
      {sheet(setupOpen, setSetupOpen, setupBody, "Set up a test clean")}
    </>
  );
}
